/**
 * RECENT ACTIVITY — turning an event log into a feed a person can read.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT WAS ON SCREEN
 *
 * Taylor, 2026-09-08: "in the recent activity it just repeats and doesnt look
 * good, like this should unique events".
 *
 * Ten consecutive rows, all one customer:
 *
 *   Changed lifecycle stage: empty → onboarding    15th Annual New Partners…  9h ago
 *   Changed lifecycle stage: onboarding → empty    15th Annual New Partners…  9h ago
 *   Changed lifecycle stage: empty → onboarding    15th Annual New Partners…  9h ago
 *   … (six more of the same two, alternating)
 *
 * TWO SEPARATE CAUSES, and fixing either alone leaves it broken:
 *
 *   1. THE FEED WASN'T THE BOOK. Home requested activity for
 *      `customers.slice(0, 20)` — the first twenty rows of the WHOLE customer
 *      list, ordered by spend, with no relation to whose book was being viewed.
 *      So the panel labelled "across the book" was across whatever twenty
 *      accounts happened to sort first, and one noisy account owned all of it.
 *
 *   2. NOTHING COLLAPSED REPEATS. The underlying events are real — something
 *      genuinely flipped that field back and forth (a test account writing in a
 *      loop). But "a field flapped nine times" is ONE thing that happened, and
 *      rendering it as nine rows buries every other event on the page.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THIS DOES NOT DO: HIDE EVENTS
 *
 * Nothing is dropped for being uninteresting. Repeats are COLLAPSED and carry
 * their count and time span, so "×9" is still nine events and the feed says so.
 * The only cap is per-customer, so a single account cannot crowd out the rest —
 * and when it bites, the entry says how many more there were rather than
 * silently ending. A feed that quietly omits things is worse than a repetitive
 * one, because you stop being able to trust it.
 */

/** An event as /api/activity returns it. */
export interface RawActivityEvent {
  id: string;
  customer_id: string;
  kind: string;
  at: string;
  actor: string;
  summary: string;
}

export interface FeedEntry {
  /** Stable key for React — the newest event's id in the collapsed group. */
  id: string;
  customerId: string;
  customerName: string;
  kind: string;
  actor: string;
  /** Newest event in the group; what the relative timestamp renders from. */
  at: string;
  /** Oldest event in the group, so a collapsed run can show its span. */
  firstAt: string;
  /** What to display. For a flap, a summary OF the flapping. */
  summary: string;
  /** 1 for a single event; >1 when repeats were collapsed. */
  repeatCount: number;
  /** True when the group was two values alternating, not one value repeating. */
  flapping: boolean;
  /** The distinct summaries that were collapsed, for a tooltip / detail line. */
  variants: string[];
}

export interface CondenseOptions {
  /** customer_id → display name, for the link label. */
  names?: Map<string, string>;
  /** Most entries to return overall. */
  limit?: number;
  /**
   * Most entries any ONE customer may contribute.
   *
   * Three is deliberate: enough that a genuinely busy account shows a real
   * picture, few enough that it cannot become the whole panel. When it bites,
   * the caller is told via `suppressedByCustomer` so the UI can offer a link
   * rather than just ending the list.
   */
  maxPerCustomer?: number;
}

export interface CondenseResult {
  entries: FeedEntry[];
  /** customer_id → how many further entries that customer had. */
  suppressedByCustomer: Map<string, number>;
  /** How many raw events the entries above represent. */
  rawCount: number;
}

/**
 * The core: newest-first, de-duplicated, repeats collapsed, per-customer capped.
 */
export function condenseActivity(
  events: readonly RawActivityEvent[],
  opts: CondenseOptions = {},
): CondenseResult {
  const { names, limit = 20, maxPerCustomer = 3 } = opts;

  // 1. Drop exact re-deliveries. The endpoint unions four table reads, so the
  //    same event id can legitimately arrive twice; rendering it twice is a bug
  //    every time.
  const seenIds = new Set<string>();
  const unique = events.filter((e) => {
    if (!e || !e.id) return false;
    if (seenIds.has(e.id)) return false;
    seenIds.add(e.id);
    return true;
  });

  // 2. Newest first. Ties broken by id so the order is stable across renders —
  //    an unstable sort makes the list appear to shuffle on every poll.
  const sorted = [...unique].sort((a, b) => {
    if (a.at !== b.at) return a.at > b.at ? -1 : 1;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });

  /**
   * 3. Collapse CONSECUTIVE events that share a customer and a kind.
   *
   * Consecutive rather than global on purpose. Two unrelated lifecycle changes
   * a month apart are two events and should read as two; the same field
   * thrashing within one run is one story. Grouping globally would merge the
   * month-apart pair and lose real history.
   */
  const groups: RawActivityEvent[][] = [];
  for (const e of sorted) {
    const last = groups[groups.length - 1];
    const head = last?.[0];
    if (head && head.customer_id === e.customer_id && head.kind === e.kind) {
      last.push(e);
    } else {
      groups.push([e]);
    }
  }

  const all: FeedEntry[] = groups.map((group) => {
    const newest = group[0];
    const oldest = group[group.length - 1];
    const variants = [...new Set(group.map((g) => g.summary))];

    /**
     * A FLAP is two values alternating, which is what was on screen. Detected
     * as "more than two events but only two distinct summaries" — describing
     * that as "empty ↔ onboarding, 9 times" is both shorter and more true than
     * nine rows, because the interesting fact is the oscillation itself.
     */
    const flapping = group.length > 2 && variants.length === 2;

    let summary: string;
    if (flapping) {
      const pair = flapEndpoints(variants);
      summary = pair
        ? `${describeKind(newest.kind)} flipped between ${pair[0]} and ${pair[1]}`
        : `${describeKind(newest.kind)} changed repeatedly`;
    } else if (variants.length === 1) {
      summary = newest.summary;
    } else {
      // A genuine run of DIFFERENT changes on one field. Lead with the newest
      // and say how many preceded it, rather than inventing a summary of them.
      summary = newest.summary;
    }

    return {
      id: newest.id,
      customerId: newest.customer_id,
      customerName: names?.get(newest.customer_id) ?? newest.customer_id,
      kind: newest.kind,
      actor: newest.actor,
      at: newest.at,
      firstAt: oldest.at,
      summary,
      repeatCount: group.length,
      flapping,
      variants,
    };
  });

  /**
   * 4. Per-customer cap. Applied AFTER collapsing, so a flapping account is
   *    already one entry and the cap only bites on an account that is genuinely
   *    doing many different things.
   */
  const perCustomer = new Map<string, number>();
  const suppressedByCustomer = new Map<string, number>();
  const kept: FeedEntry[] = [];
  for (const entry of all) {
    const n = perCustomer.get(entry.customerId) ?? 0;
    if (n >= maxPerCustomer) {
      suppressedByCustomer.set(
        entry.customerId,
        (suppressedByCustomer.get(entry.customerId) ?? 0) + 1,
      );
      continue;
    }
    perCustomer.set(entry.customerId, n + 1);
    kept.push(entry);
  }

  const entries = kept.slice(0, limit);
  // Anything trimmed by the overall limit is also reported per customer, so the
  // UI's "N more" is the truth and not just the per-customer half of it.
  for (const entry of kept.slice(limit)) {
    suppressedByCustomer.set(
      entry.customerId,
      (suppressedByCustomer.get(entry.customerId) ?? 0) + 1,
    );
  }

  return {
    entries,
    suppressedByCustomer,
    rawCount: entries.reduce((s, e) => s + e.repeatCount, 0),
  };
}

/**
 * Pull the two endpoints out of a flapping pair's summaries.
 *
 * The events read "Changed lifecycle stage: empty → onboarding", so the two
 * distinct values are recoverable from either side of the arrow. Returns null
 * when the summaries are not arrow-shaped, and the caller falls back to a
 * generic phrasing rather than printing something wrong.
 */
function flapEndpoints(variants: string[]): [string, string] | null {
  if (variants.length !== 2) return null;
  const parts = variants
    .map((v) => v.split(/\s*(?:→|->)\s*/))
    .filter((p) => p.length === 2);
  if (parts.length !== 2) return null;

  // Take the two right-hand sides — the values it landed on — and strip any
  // "Changed lifecycle stage:" prefix that rode along on the left.
  const a = parts[0][1].trim();
  const b = parts[1][1].trim();
  if (!a || !b || a === b) return null;
  return [a, b];
}

/** "lifecycle_stage" / "Changed lifecycle stage: x → y" → "Lifecycle stage". */
function describeKind(kind: string): string {
  const cleaned = (kind || 'field').replace(/[_-]+/g, ' ').trim();
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

/**
 * Which customers to ask /api/activity about.
 *
 * THE FEED FOLLOWS THE BOOK. This exists because Home passed
 * `customers.slice(0, 20)` — the first twenty of the whole list — and then
 * labelled the panel "across the book". Now the caller passes the book in view
 * and gets back the ids to request.
 *
 * Ordered by spend (the list already arrives that way) and capped, because the
 * endpoint takes the ids as a query string and an unbounded book would build a
 * URL nothing will accept. The cap is reported so the panel can say it is
 * showing the top of a large book rather than implying it covers all of it.
 */
export function activityCustomerIds(
  book: ReadonlyArray<{ customer_id: string }>,
  max = 40,
): { ids: string[]; covered: number; total: number } {
  const ids = book.slice(0, max).map((c) => c.customer_id);
  return { ids, covered: ids.length, total: book.length };
}
